// Regras puras do header principal (saudação, plano, busca ⌘K, tempo
// relativo, silêncio de notificações). Sem React.
import { differenceInMinutes, format } from "date-fns";
import { i18n } from "../../translate/i18n";
import { diasAteVencimento } from "../../pages/Companies/companyRules";

export const greeting = (name, now = new Date()) => {
  const h = now.getHours();
  const g = i18n.t(h < 12 ? "aeros.header.morning" : h < 18 ? "aeros.header.afternoon" : "aeros.header.evening");
  const first = (name || "").trim().split(" ")[0];
  return first ? `${g}, ${first}` : g;
};

export const planLabel = (company, dateToClient = (d) => d) => {
  if (!company?.name) return "";
  if (!company.dueDate) return company.name;
  return i18n.t("aeros.header.plan", { company: company.name, date: dateToClient(company.dueDate) });
};

export const LANGUAGES = [
  { code: "pt-BR", short: "PT", label: "Português (Brasil)" },
  { code: "en", short: "EN", label: "English" },
  { code: "es", short: "ES", label: "Español" },
  { code: "ar", short: "AR", label: "عربي" },
];
export const langShort = (code) => (LANGUAGES.find((l) => l.code === code) || LANGUAGES[0]).short;

export const relativeTime = (iso, now = new Date()) => {
  if (!iso) return "";
  const min = differenceInMinutes(now, new Date(iso));
  if (min < 1) return "agora";
  if (min < 60) return `${min} min`;
  if (min < 1440) return `${Math.floor(min / 60)} h`;
  return format(new Date(iso), "dd/MM");
};

// Telas navegáveis pela busca ⌘K (rota + rótulo + palavras-chave).
// Rótulos vêm do i18n (aeros.screens.*) no momento da busca.
export const SCREENS = [
  ["/", "dashboard", "inicio painel dashboard"],
  ["/tickets", "tickets", "tickets chamados atendimentos"],
  ["/moments", "moments", "moments tempo real painel ao vivo live panel"],
  ["/kanban", "kanban", "quadro"],
  ["/schedules", "schedules", "agendar agendamentos"],
  ["/quick-messages", "quickMessages", "atalhos respostas rapidas quick replies"],
  ["/contacts", "contacts", "clientes contatos"],
  ["/tags", "tags", "etiquetas"],
  ["/chats", "chats", "equipe chat interno"],
  ["/reports", "reports", "report relatorios"],
  ["/wallets", "wallets", "carteiras"],
  ["/helps", "helps", "tutoriais videos ajuda help"],
  ["/campaigns", "campaigns", "disparo campanhas"],
  ["/flowbuilders", "flowbuilders", "fluxo"],
  ["/prompts", "prompts", "ia prompts openai"],
  ["/users", "users", "atendentes usuarios"],
  ["/connections", "connections", "whatsapp conexoes"],
  ["/queues", "queues", "filas chatbot"],
  ["/settings", "settings", "ajustes configuracoes"],
  ["/financeiro", "financeiro", "assinatura subscription suscripcion faturas financeiro billing"],
].map(([path, key, keys]) => ({ path, key, keys }));

const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

export const matchScreens = (query, screens = SCREENS) => {
  const q = norm(query).trim();
  const labeled = screens.map((s) => ({ ...s, label: i18n.t(`aeros.screens.${s.key}`) }));
  if (!q) return labeled.slice(0, 6);
  return labeled.filter((s) => norm(`${s.label} ${s.keys} ${s.path}`).includes(q)).slice(0, 6);
};

// "Silenciar até HH:MM" — devolve o Date de hoje (ou amanhã se já passou).
export const muteUntil = (hhmm, now = new Date()) => {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(now);
  d.setHours(h, m, 0, 0);
  if (d <= now) d.setDate(d.getDate() + 1);
  return d;
};

export const isMuted = (untilIso, now = new Date()) => Boolean(untilIso) && new Date(untilIso) > now;

// Abas do popover de notificações.
export const filterNotifications = (tickets, tab, meId) => {
  const list = tickets || [];
  if (tab === "mine") return list.filter((t) => Number(t.userId) === Number(meId) && !t.isGroup);
  if (tab === "queue") return list.filter((t) => t.status === "pending" && !t.isGroup);
  if (tab === "groups") return list.filter((t) => t.isGroup);
  return list;
};

// Botão "Pagar fatura" no cabeçalho: só o admin da empresa busca a fatura
// aberta (GET /invoices/open); o backend também devolve tudo nulo para os
// demais perfis.
export const deveBuscarFaturaAberta = (user) => user?.profile === "admin";

// Mostra o botão quando há fatura aberta e a empresa está no trial (hoje ≤
// trialAte) ou a até 7 dias do vencimento (vencida inclusive). O backend só
// devolve trialAte enquanto a fatura aberta é a que fecha o trial — pago no
// trial, o botão só volta perto do novo vencimento. A empresa 1
// (dona da plataforma) nunca vê. Datas YYYY-MM-DD são comparadas como dia
// local — `new Date("YYYY-MM-DD")` é UTC e cai no dia anterior em fuso negativo.
export const mostraPagarFatura = ({ company, invoice, dueDate, trialAte, hoje = new Date() } = {}) => {
  if (!invoice) return false;
  if (Number(company?.id) === 1) return false;
  if (trialAte) {
    const dias = diasAteVencimento({ dueDate: trialAte }, hoje);
    if (dias !== null && dias >= 0) return true;
  }
  if (dueDate) {
    const dias = diasAteVencimento({ dueDate }, hoje);
    if (dias !== null && dias <= 7) return true;
  }
  return false;
};

// Faixa de problema de conta Meta (erro real da Meta, Task 8). Só o admin vê;
// lista as conexões marcadas (accountIssueCode) que ele ainda não dispensou
// nesta sessão do navegador. A chave de dispensa inclui accountIssueAt: um
// problema NOVO na mesma conexão volta a aparecer.
export const conexoesComProblemaDeConta = (whatsApps) =>
  (Array.isArray(whatsApps) ? whatsApps : []).filter(
    (w) => w && w.accountIssueCode !== null && w.accountIssueCode !== undefined && w.accountIssueCode !== ""
  );

export const chaveDispensaContaMeta = (w) => `${w?.id}:${w?.accountIssueAt || ""}`;

export const mostraFaixaContaMeta = (user, whatsApps, dispensadas = new Set()) => {
  if (user?.profile !== "admin") return false;
  return conexoesComProblemaDeConta(whatsApps).some((w) => !dispensadas.has(chaveDispensaContaMeta(w)));
};
